import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './AdminResumePublish.css';
import './OperationDashboard.css';
import './AdminResumeManage.css';
import AdminResumeTemplatesApi from '../services/adminResumeTemplatesApi';
import AdminResumeTemplatesStatsApi from '../services/adminResumeTemplatesStatsApi';
import AlertModal from '../components/common/AlertModal';
import resumeTemplatesApi from '../services/resumeTemplatesApi';
import { formatBeijingDateTime } from '../utils/dateTimeBeijing';

// 简历类型选项（固定）
const RESUME_TYPE_OPTIONS = [
  { value: '', label: '请选择类型（可选）' },
  { value: '单页', label: '单页' },
  { value: '多页', label: '多页' },
  { value: '表格', label: '表格' },
];

const DEFAULT_PAGE_SIZE = 10;

const AdminResumeManage = () => {
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [stats, setStats] = useState(null);

  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [keyword, setKeyword] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [name, setName] = useState('');
  const [intro, setIntro] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [resumeType, setResumeType] = useState('');

  const [templateFile, setTemplateFile] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]); // 编辑模式下预览图列表
  const [coverIndex, setCoverIndex] = useState(0);
  const [originalCoverIndex, setOriginalCoverIndex] = useState(0);

  // 分类选项（从API获取）
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const normalizePreviewPath = useCallback((p) => {
    if (!p) return '';
    const raw = String(p).replace(/^\//, '');
    
    // 如果已经是完整的URL（OSS或CDN），直接返回
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return raw;
    }
    
    // 预览图静态资源挂在后端的 /assets 下
    // 开发环境通常前后端端口不同，需要把图片请求指向后端同源(同 host:port)
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const apiPort = process.env.REACT_APP_API_PORT || '8000';
    const base = process.env.NODE_ENV === 'production' ? '' : `${protocol}//${hostname}:${apiPort}`;
    
    if (raw.startsWith('assets/')) {
      return `${base}/${raw}`;
    }
    return `${base}/assets/${raw}`;
  }, []);
  const coverPageNo = useMemo(() => coverIndex + 1, [coverIndex]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);


  const resetForm = () => {
    setName('');
    setIntro('');
    setCategory('');
    setTags('');
    setResumeType('');
    setTemplateFile(null);
    setPreviewFiles([]);
    setCoverIndex(0);
    setError('');
    setSuccessMsg('');
    setSubmitting(false);
    setEditingId(null);
  };

  const loadStats = async () => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const res = await AdminResumeTemplatesStatsApi.getStats();
      const data = res?.data;
      if (data?.success) {
        setStats(data.data || null);
      } else {
        setStats(null);
        setStatsError(data?.message || '获取看板数据失败');
      }
    } catch (e) {
      setStats(null);
      const detail = e?.response?.data?.detail;
      const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
      setStatsError(safeDetail || e?.message || '获取看板数据失败');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadList = async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await AdminResumeTemplatesApi.list({
        page,
        page_size: pageSize,
        search: keyword || undefined,
      });
      const data = res?.data;
      if (data?.success) {
        setItems(data?.data?.items || []);
        setTotal(Number(data?.data?.pagination?.total || 0));
      } else {
        setItems([]);
        setTotal(0);
        setListError(data?.message || '获取列表失败');
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      const detail = e?.response?.data?.detail;
      const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
      setListError(safeDetail || e?.message || '获取列表失败');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, keyword]);

  const totalPages = useMemo(() => {
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  // 加载分类选项
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await resumeTemplatesApi.getFilters();
      const categories = res?.data?.categories || [];
      setCategoryOptions(categories);
    } catch (e) {
      console.error('加载分类选项失败:', e);
      // 失败时使用默认分类
      setCategoryOptions(['简约', '商务', '创意', '专业', '多页完整', '表格通用']);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const movePreview = (from, to) => {
    if (to < 0 || to >= previewFiles.length) return;
    setPreviewFiles((prev) => {
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });

    setCoverIndex((prev) => {
      if (prev === from) return to;
      if (from < prev && to >= prev) return prev - 1;
      if (from > prev && to <= prev) return prev + 1;
      return prev;
    });
  };

  const removePreview = (idx) => {
    setPreviewFiles((prev) => prev.filter((_, i) => i !== idx));
    setCoverIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return prev - 1;
      return prev;
    });
  };

  const openCreate = () => {
    resetForm();
    setShowCreate(true);
  };

  const openEdit = async (id) => {
    resetForm();
    setShowEdit(true);
    setEditingId(id);
    setSubmitting(true);
    // 加载预览图列表
    setPreviewImages([]);
    try {
      const res = await AdminResumeTemplatesApi.getDetail(id);
      const data = res?.data;
      if (!data?.success) throw new Error(data?.message || '获取详情失败');
      const d = data?.data || {};
      // 处理预览图
      let imgs = Array.isArray(d.preview_images) ? d.preview_images.slice() : [];
      imgs = imgs
        .sort((a, b) => (a?.page_no ?? 0) - (b?.page_no ?? 0))
        .map((x) => {
          const imagePath = x?.image_path || '';
          const normalizedPath = normalizePreviewPath(imagePath);
          // 调试日志
          if (process.env.NODE_ENV === 'development') {
            console.log('预览图路径处理:', {
              original: imagePath,
              normalized: normalizedPath,
              page_no: x?.page_no
            });
          }
          return {
            page_no: x?.page_no,
            src: normalizedPath,
          };
        })
        .filter((x) => x.page_no && x.src);
      if (!imgs.length && d.preview_image_path) {
        const fallbackPath = normalizePreviewPath(d.preview_image_path);
        if (fallbackPath) {
          imgs.push({ page_no: 1, src: fallbackPath });
        }
      }
      // 调试日志
      if (process.env.NODE_ENV === 'development') {
        console.log('最终预览图列表:', imgs);
      }
      setPreviewImages(imgs);

      setName(d.name || '');
      setIntro(d.intro || '');
      setCategory(d.category || '');
      setTags(d.tags || '');
      setResumeType(d.resume_type || '');
      const cp = Number(d.cover_page_no || 1);
      setOriginalCoverIndex(Math.max(0, cp - 1));
      setCoverIndex(Math.max(0, cp - 1));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
      setError(safeDetail || e?.message || '获取详情失败');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowCreate(false);
    setShowEdit(false);
    resetForm();
  };

  const submitCreate = async () => {
    if (!name.trim()) {
      setError('请填写模板名称');
      return;
    }
    if (!templateFile) {
      setError('请上传简历模板文件');
      return;
    }
    if (!previewFiles.length) {
      setError('请至少上传1张预览图');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMsg('');

    try {
      const createPayload = { name };
      const createRes = await AdminResumeTemplatesApi.create(createPayload);
      const tplId = createRes?.data?.data?.id;
      if (!tplId) throw new Error('创建模板失败：未返回ID');

      await AdminResumeTemplatesApi.uploadFile(tplId, templateFile);
      await AdminResumeTemplatesApi.uploadPreviews(tplId, previewFiles);
      await AdminResumeTemplatesApi.setCover(tplId, coverPageNo);

      const updatePayload = {
        name,
        intro: intro || undefined,
        category: category || undefined,
        tags: tags || undefined,
        resume_type: resumeType || undefined,
        cover_page_no: coverPageNo,
      };
      await AdminResumeTemplatesApi.update(tplId, updatePayload);

      setSuccessMsg(`发布成功（模板ID：${tplId}）`);
      await Promise.all([loadStats(), loadList()]);
      closeModal();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
      setError(safeDetail || e?.message || '发布失败');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!editingId) return;
    if (!name.trim()) {
      setError('请填写模板名称');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMsg('');

    try {
      const updatePayload = {
        name,
        intro: intro || undefined,
        category: category || undefined,
        tags: tags || undefined,
        resume_type: resumeType || undefined,
        cover_page_no: coverPageNo,
      };
      await AdminResumeTemplatesApi.update(editingId, updatePayload);
      setSuccessMsg('更新成功');
      await Promise.all([loadStats(), loadList()]);
      closeModal();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
      setError(safeDetail || e?.message || '更新失败');
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const doDelete = async () => {
    const id = deleteId;
    setConfirmDeleteOpen(false);
    setDeleteId(null);
    if (!id) return;

    try {
      await AdminResumeTemplatesApi.delete(id);
      await Promise.all([loadStats(), loadList()]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
      setListError(safeDetail || e?.message || '删除失败');
    }
  };

  return (
    <div className="resume-manage">
      <h2>简历发布管理</h2>

      <div className="resume-overview">
        {statsError ? <div className="resume-alert error">{statsError}</div> : null}

        <div className="overview-grid">
          <div className="overview-card">
            <div>
              <div className="overview-label">模板总数</div>
              <div className="overview-value">{statsLoading ? '...' : stats?.total ?? '-'}</div>
            </div>
          </div>
          <div className="overview-card">
            <div>
              <div className="overview-label">今日新增</div>
              <div className="overview-value">{statsLoading ? '...' : stats?.today_new ?? '-'}</div>
            </div>
          </div>

          <div className="overview-card">
            <div>
              <div className="overview-label">总下载量</div>
              <div className="overview-value">{statsLoading ? '...' : stats?.total_downloads ?? '-'}</div>
            </div>
          </div>
          <div className="overview-card">
            <div>
              <div className="overview-label">总浏览量</div>
              <div className="overview-value">{statsLoading ? '...' : stats?.total_views ?? '-'}</div>
            </div>
          </div>
          <div className="overview-card">
            <div>
              <div className="overview-label">总点赞量</div>
              <div className="overview-value">{statsLoading ? '...' : stats?.total_likes ?? '-'}</div>
            </div>
          </div>

        </div>
      </div>

      <div className="resume-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{ fontWeight: 800 }}>关键词</div>
            <input
              className="resume-input"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder="输入名称/分类/标签关键词"
              style={{ maxWidth: 360 }}
            />
            <button
              className="btn"
              onClick={() => {
                setPage(1);
                setKeyword(keywordInput.trim());
              }}
            >
              查询
            </button>
            <button
              className="btn"
              onClick={() => {
                setKeywordInput('');
                setPage(1);
                setKeyword('');
              }}
            >
              清空
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ color: '#6b7280', fontSize: 12 }}>共 {total} 条记录</div>
            <button className="resume-primary-btn" onClick={openCreate}>
              创建简历
            </button>
          </div>
        </div>
      </div>

      {listError ? <div className="resume-alert error">{listError}</div> : null}

      <div className="resume-card">
        <div style={{ fontWeight: 800, marginBottom: 12 }}>模板列表</div>

        {listLoading ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>加载中...</div>
        ) : items.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>暂无数据</div>
        ) : (
          <div className="news-table">
            <div className="news-table-header">
              <div className="news-col-id">序号</div>
              <div className="news-col-title">名称</div>
              <div className="news-col-time">下载量</div>
              <div className="news-col-time">浏览量</div>
              <div className="news-col-time">点赞量</div>
              <div className="news-col-time">创建时间</div>
              <div className="news-col-actions">操作</div>
            </div>
            {items.map((x, index) => (
              <div key={x.id} className="news-table-row">
                <div className="news-col-id">{(page - 1) * pageSize + index + 1}</div>
                <div className="news-col-title" title={x.name}>{x.name}</div>
                <div className="news-col-time">{x.download_count ?? 0}</div>
                <div className="news-col-time">{x.view_count ?? 0}</div>
                <div className="news-col-time">{x.like_count ?? 0}</div>
                <div className="news-col-time">{x.created_at ? formatBeijingDateTime(x.created_at) : '-'}</div>
                <div className="news-col-actions">
                  <button className="btn-table btn-secondary" onClick={() => openEdit(x.id)}>
                    编辑
                  </button>
                  <button className="btn-table btn-danger" onClick={() => askDelete(x.id)}>
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ color: '#6b7280', fontSize: 12 }}>
            第 {page} / {totalPages} 页
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              上一页
            </button>
            <button className="btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              下一页
            </button>
          </div>
        </div>
      </div>

      {(showCreate || showEdit) ? (
        <div className="resume-modal-mask" onClick={closeModal}>
          <div className="resume-modal" onClick={(e) => e.stopPropagation()}>
            <div className="resume-modal-header">
              <div style={{ fontWeight: 900, fontSize: 18 }}>
                {showEdit ? '编辑简历模板' : '创建简历模板'}
              </div>
              <button className="btn" onClick={closeModal}>
                关闭
              </button>
            </div>

            {error ? <div className="resume-alert error" style={{ marginTop: 12 }}>{error}</div> : null}
            {successMsg ? <div className="resume-alert success" style={{ marginTop: 12 }}>{successMsg}</div> : null}

            <div className="resume-modal-body">
              <div className="resume-publish-grid" style={{ gridTemplateColumns: showEdit ? '1fr 1fr' : undefined }}>
                <div className="resume-card" style={{ margin: 0 }}>
                  {!showEdit ? (
                    // 创建模式下的上传/管理区域

                    <>
                      <div className="resume-form">
                        <div className="resume-label">模板文件</div>
                        <input
                          type="file"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setTemplateFile(f);
                            if (f) {
                              const base = String(f.name || '').replace(/\.[^/.]+$/, '').trim();
                              if (base && !name.trim()) setName(base);
                            }
                          }}
                        />

                        <div className="resume-label">预览图</div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const arr = Array.from(e.target.files || []);
                            setPreviewFiles((prev) => prev.concat(arr));
                          }}
                        />
                      </div>

                      <div className="resume-divider" />
                    </>
                  ) : null}

                  <div className="resume-form">
                    <div className="resume-label">模板名称</div>
                    <input className="resume-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：黑色通用" />

                    <div className="resume-label">简介</div>
                    <textarea className="resume-textarea" value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} placeholder="简要介绍" />

                    <div className="resume-label">分类</div>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="resume-input"
                        type="text"
                        list="category-list"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="请选择或输入分类"
                        style={{
                          width: '100%',
                        }}
                      />
                      <datalist id="category-list">
                        {categoryOptions.map((cat) => (
                          <option key={cat} value={cat} />
                        ))}
                      </datalist>
                    </div>

                    <div className="resume-label">标签</div>
                    <input className="resume-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="用逗号分隔，例如：黑色,简约,A4" />

                    <div className="resume-label">类型</div>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="resume-input"
                        type="text"
                        list="resume-type-list"
                        value={resumeType}
                        onChange={(e) => setResumeType(e.target.value)}
                        placeholder="请选择或输入类型"
                        style={{
                          width: '100%',
                        }}
                      />
                      <datalist id="resume-type-list">
                        {RESUME_TYPE_OPTIONS.filter(opt => opt.value).map((option) => (
                          <option key={option.value} value={option.value} />
                        ))}
                      </datalist>
                    </div>



                    <div className="resume-modal-footer">
                      <button className="btn" onClick={closeModal} disabled={submitting}>
                        取消
                      </button>
                      {showEdit ? (
                        <button className="resume-primary-btn" onClick={submitEdit} disabled={submitting}>
                          {submitting ? '保存中...' : '保存修改'}
                        </button>
                      ) : (
                        <button className="resume-primary-btn" onClick={submitCreate} disabled={submitting}>
                          {submitting ? '发布中...' : '发布'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {!showEdit ? (
                  <div className="resume-card" style={{ margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ fontWeight: 800 }}>预览图管理</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>封面：第 {coverPageNo} 页</div>
                    </div>

                    {previewFiles.length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: 14 }}>暂无预览图，请先上传。</div>
                    ) : (
                      <div className="preview-list">
                        {previewFiles.map((f, idx) => {
                          const url = URL.createObjectURL(f);
                          const isCover = idx === coverIndex;
                          return (
                            <div key={`${f.name}-${idx}`} className={`preview-item ${isCover ? 'cover' : ''}`}>
                              <img src={url} alt={f.name} className="preview-thumb" />

                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                  <div style={{ fontWeight: 800, fontSize: 13, color: '#111827' }}>
                                    第 {idx + 1} 页{isCover ? '（封面）' : ''}
                                  </div>
                                  <button onClick={() => removePreview(idx)} className="btn btn-danger">
                                    删除
                                  </button>
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, wordBreak: 'break-all' }}>{f.name}</div>

                                <div className="preview-actions">
                                  <button onClick={() => setCoverIndex(idx)} className={`btn ${isCover ? 'btn-cover' : ''}`}>
                                    设为封面
                                  </button>
                                  <button onClick={() => movePreview(idx, idx - 1)} className="btn">
                                    上移
                                  </button>
                                  <button onClick={() => movePreview(idx, idx + 1)} className="btn">
                                    下移
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}

                {showEdit ? (
                  // ====== 编辑模式-右侧预览展示 ======
                  <div className="resume-card" style={{ margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ fontWeight: 800 }}>预览图</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>封面：第 {originalCoverIndex + 1} 页</div>
                    </div>

                    {previewImages.length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: 14 }}>暂无预览图</div>
                    ) : (
                      <div className="preview-list">
                        {previewImages.map((img, idx) => {
                          const isCover = idx === originalCoverIndex;
                          return (
                            <div
                              key={`preview-${img.page_no}-${idx}`}
                              className={`preview-item ${isCover ? 'cover' : ''}`}
                              style={{ cursor: 'pointer' }}
                              onClick={async () => {
                                if (!editingId) return;
                                setError('');
                                setSuccessMsg('');
                                try {
                                  const res = await AdminResumeTemplatesApi.setCover(editingId, img.page_no);
                                  const data = res?.data;
                                  if (!data?.success) throw new Error(data?.message || '设置封面失败');
                                  setOriginalCoverIndex(idx);
                                  setCoverIndex(idx);
                                } catch (e) {
                                  const detail = e?.response?.data?.detail;
                                  const safeDetail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
                                  setError(safeDetail || e?.message || '设置封面失败');
                                } finally {
                                }
                              }}
                            >
                              {img.src ? (
                                <img 
                                  src={img.src} 
                                  alt={`第${img.page_no}页`} 
                                  className="preview-thumb"
                                  style={{ 
                                    display: 'block',
                                    width: '72px',
                                    height: '100px',
                                    objectFit: 'contain',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '8px'
                                  }}
                                  onError={(e) => {
                                    console.error(`预览图加载失败: ${img.src}`, e);
                                    // 隐藏原图片
                                    e.target.style.display = 'none';
                                    // 检查是否已经有错误提示
                                    const parent = e.target.parentNode;
                                    if (!parent.querySelector('.preview-error')) {
                                      const errorDiv = document.createElement('div');
                                      errorDiv.className = 'preview-error preview-thumb';
                                      errorDiv.style.cssText = `
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        width: 72px;
                                        height: 100px;
                                        color: #9ca3af;
                                        font-size: 12px;
                                        background: #f3f4f6;
                                        border-radius: 8px;
                                        text-align: center;
                                        padding: 8px;
                                        box-sizing: border-box;
                                      `;
                                      errorDiv.textContent = '图片加载失败';
                                      parent.insertBefore(errorDiv, e.target);
                                    }
                                  }}
                                  onLoad={(e) => {
                                    // 图片加载成功，移除可能的错误提示
                                    const parent = e.target.parentNode;
                                    if (parent) {
                                      const errorDiv = parent.querySelector('.preview-error');
                                      if (errorDiv) {
                                        errorDiv.remove();
                                      }
                                    }
                                  }}
                                />
                              ) : (
                                <div 
                                  className="preview-thumb"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '72px',
                                    height: '100px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '8px',
                                    color: '#9ca3af',
                                    fontSize: '12px'
                                  }}
                                >
                                  无图片
                                </div>
                              )}
                              <div style={{ fontWeight: 800, fontSize: 13, color: '#111827', marginTop: 4 }}>
                                第 {img.page_no} 页{isCover ? '（封面）' : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AlertModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="确认删除"
        message="确定要删除该简历模板吗？"
        type="warning"
        showCancel
        cancelText="取消"
        confirmText="删除"
        onConfirm={doDelete}
      />
    </div>
  );
};

export default AdminResumeManage;

